import {useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {PlanComparisonTableWrapper} from 'shells/billing/plan-comparison/plan-comparison-table-wrapper';
import {PlanPicker} from 'shells/billing/plans/plan-picker';
import type {Cycle, AddOns, SelectedAddOn} from 'shells/billing/types';

import type {GetAvailablePlans_recurlyAvailablePlans_marketingSuite_plans as Plan} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../../../analytics-client';
import {OpenableModals} from '../../../../openable-modals/openable-modals';
import * as actions from '../../../../openable-modals/openable-modals-actions';

import {useGetPlanComparisons} from '../../graphql/hooks';

import {
    getPlanCodeForGivenCycle,
    handlePlanIsEnabledChange,
    handleSelectedPlanChange,
} from '../helpers';
import {SuiteHeader} from '../suite-header/suite-header';

type Props = {
    plans: Plan[];
    cycle: Cycle;
    setCycle: (cycle: Cycle) => void;
    errorMessage: string | null | undefined;
    isLoadingPlans: boolean;
    selectedPlanCode: string | null | undefined;
    setSelectedMarketingPlanCode: (planCode: string) => void;
    numSalesUsers: string;
    addOns: AddOns | null | undefined;
    selectedAddOns: SelectedAddOn[];
    setSelectedAddOns: any;
    isTrial: boolean;
    scrollContainerId: string;
};

export function MarketingSuitePicker(props: Props) {
    const {planComparisons} = useGetPlanComparisons('marketing');

    const cyclePlans = props.plans.filter((plan) => plan.cycleText === props.cycle);

    const handleCycleChange = (cycle: Cycle) => {
        if (cycle !== props.cycle && props.selectedPlanCode) {
            const updatedPlanCode = getPlanCodeForGivenCycle(props.selectedPlanCode, cycle);
            props.setSelectedMarketingPlanCode(updatedPlanCode);
        }

        props.setCycle(cycle);
    };
    const dispatch = useDispatch();

    const handleOpenModal = () => {
        dispatch(actions.openModal(OpenableModals.CAMPAIGNS_ONBOARDING_MODAL));
    };

    useEffect(() => {
        if (props.isTrial)
            analyticsClient.track(
                analyticsClient.EVENTS.TRACK_CUSTOMER_TRIAL_VISITS_BILLING_MARKETING
            );
    }, [props.isTrial]);

    return (
        <div className='pad-16 flex flex-dir-col full-width gap-8 border-box'>
            <SuiteHeader
                suite='marketing'
                selectedCycle={props.cycle}
                onCycleChange={handleCycleChange}
                isMonthlyOnly={true}
                hasUsersInput={false}
                numSalesUsers={props.numSalesUsers}
                hideUsersInput={true}
            />
            <PlanPicker
                variant='marketing'
                plans={cyclePlans}
                selectedPlanCode={props.selectedPlanCode}
                onSelectedPlanCodeChange={props.setSelectedMarketingPlanCode}
                isLoading={props.isLoadingPlans}
                errorMessage={props.errorMessage}
                addOns={props.addOns}
                selectedAddOns={props.selectedAddOns}
                setSelectedAddOns={props.setSelectedAddOns}
                onSelectedPlanChange={handleSelectedPlanChange}
                onPlanIsEnabledChange={handlePlanIsEnabledChange}
                onOpenModal={handleOpenModal}
            />
            {!props.isLoadingPlans && planComparisons && (
                <PlanComparisonTableWrapper
                    planComparisons={planComparisons}
                    scrollContainerId={props.scrollContainerId}
                />
            )}
        </div>
    );
}
